import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useAuth } from "../../../context/AuthContext";
import { API_URL } from "./constants";
import "./CopyWeekTab.css";
import { fetchCaptainSocialEvents } from "../../../graphql/client";

// Admin Copy Week App: pick a source date range (default: the last 7
// days), tick the events to repeat, optionally tweak title / time /
// location per event, and copy them forward by a configurable number
// of days in one click. The shift auto-suggests the smallest multiple
// of 7 that lands the range in the future — so copying last week is
// +7, a week from 3 weeks ago is +21, always same-weekday — but it's
// manually overridable for odd cases (with a weekday-shift warning).
//
// Creation goes through the same socialevents POST the Create Event
// page uses — the backend generates the new socialEventId from the new
// date + title and stamps createdBy from the admin's token. The poll
// field is deliberately NOT copied: last week's GroupMe poll belongs
// to last week's event.
//
// Fields the source listing doesn't expose (hosts, password) fall back
// to defaults — set hosts on the copies via Edit Events if they matter.

const SOCIALEVENTS_POST_URL =
  "https://4cljs7mcdi.execute-api.us-east-1.amazonaws.com/default/socialevents";

const WEEKDAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const MONTHS = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

// socialEventId always starts YYYY-MM-DD — the most reliable date
// source on the row (the `date` attribute isn't in the list
// projection).
const dateFromId = (socialEventId) => {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(socialEventId || "");
  if (!m) return null;
  return new Date(
    parseInt(m[1], 10),
    parseInt(m[2], 10) - 1,
    parseInt(m[3], 10)
  );
};

const addDays = (date, days) => {
  const d = new Date(date);
  d.setDate(d.getDate() + days);
  return d;
};

const toISO = (date) => {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
};

// Parse YYYY-MM-DD as a LOCAL date (new Date("YYYY-MM-DD") is UTC and
// shifts a day in negative-offset timezones).
const fromISO = (s) => {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(s || "");
  if (!m) return null;
  return new Date(
    parseInt(m[1], 10),
    parseInt(m[2], 10) - 1,
    parseInt(m[3], 10)
  );
};

const todayMidnight = () => {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  return d;
};

const daysBetween = (a, b) =>
  Math.round((b.getTime() - a.getTime()) / (24 * 60 * 60 * 1000));

// Smallest positive multiple of 7 that moves the range's END into
// today-or-later — the "same weekday, nearest future week" default.
const autoShiftFor = (toDateStr) => {
  const to = fromISO(toDateStr);
  if (!to) return 7;
  const behind = daysBetween(to, todayMidnight());
  if (behind <= 0) return 7;
  return Math.ceil(behind / 7) * 7;
};

const shortLabel = (date) =>
  `${WEEKDAYS[date.getDay()]} ${date.getMonth() + 1}/${date.getDate()}`;

// "Month D, YYYY" — the format the socialevents POST schema expects
// (matches CreateEvent.js).
const longDate = (date) =>
  `${MONTHS[date.getMonth()]} ${date.getDate()}, ${date.getFullYear()}`;

const CopyWeekTab = () => {
  const { getAuthHeaders } = useAuth();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  // Per-event working state keyed by socialEventId:
  // { selected, title, time, privateLocation, status, message }
  const [drafts, setDrafts] = useState({});
  const [copying, setCopying] = useState(false);

  // Source range (inclusive). Defaults to the last 7 days, but any
  // window works — a week from 3 weeks ago, 5 days, 9 days, or a
  // range that reaches into the future (planning early). The events
  // API caps history at 90 days back; the date input min mirrors it.
  const [fromDate, setFromDate] = useState(() =>
    toISO(addDays(todayMidnight(), -7))
  );
  const [toDate, setToDate] = useState(() =>
    toISO(addDays(todayMidnight(), -1))
  );
  // Days to shift each copy forward. Auto-follows the range (nearest
  // future same-weekday week) until the admin edits it by hand.
  const [shiftDays, setShiftDays] = useState(7);
  const [shiftDirty, setShiftDirty] = useState(false);

  useEffect(() => {
    if (!shiftDirty) setShiftDays(autoShiftFor(toDate));
  }, [toDate, shiftDirty]);

  const load = useCallback(async () => {
    const from = fromISO(fromDate);
    const to = fromISO(toDate);
    if (!from || !to) {
      setError("Pick valid From / To dates.");
      return;
    }
    if (from > to) {
      setError("From date must be on or before the To date.");
      return;
    }
    setLoading(true);
    setError("");
    try {
      const today = todayMidnight();
      // Window params are relative to today; over-fetch a little and
      // filter to the exact range client-side.
      const pastDays = Math.min(Math.max(daysBetween(from, today), 0), 90);
      const daysAhead = Math.max(daysBetween(today, to) + 1, 1);
      // #134 phase 11: graphql captainSocialEvents.
      const data = await fetchCaptainSocialEvents(daysAhead, pastDays);
      // Exact-range filter, oldest first so the list reads like a
      // planner.
      const list = (data.upcomingEvents || [])
        .filter((ev) => {
          const d = dateFromId(ev.socialEventId);
          return d && d >= from && d <= to;
        })
        .sort((a, b) =>
          (a.socialEventId || "").localeCompare(b.socialEventId || "")
        );
      setEvents(list);
      setDrafts((prev) => {
        const next = {};
        for (const ev of list) {
          next[ev.socialEventId] = prev[ev.socialEventId] || {
            selected: false,
            title: ev.title || "",
            time: ev.time || "",
            privateLocation: ev.privateLocation || "",
            status: "idle",
            message: "",
          };
        }
        return next;
      });
    } catch (err) {
      setError(err.message || "Failed to load last week's events");
    } finally {
      setLoading(false);
    }
  }, [getAuthHeaders, fromDate, toDate]);

  useEffect(() => {
    load();
  }, [load]);

  const setDraft = (id, patch) => {
    setDrafts((prev) => ({ ...prev, [id]: { ...prev[id], ...patch } }));
  };

  const selectedIds = useMemo(
    () => events.map((e) => e.socialEventId).filter((id) => drafts[id]?.selected),
    [events, drafts]
  );

  const setAll = (selected) => {
    setDrafts((prev) => {
      const next = { ...prev };
      for (const ev of events) {
        next[ev.socialEventId] = { ...next[ev.socialEventId], selected };
      }
      return next;
    });
  };

  // Build the POST payload from the source row + the admin's edits.
  // Only keys in the socialevents Joi schema — unknown keys are
  // rejected, and CRUCIALLY so are EMPTY STRINGS: Joi.string()
  // disallows "" (the schema's default("") only applies when the key
  // is absent). So optional fields are included only when they carry a
  // real value. poll is always omitted (never copy the source week's
  // poll); hosts/password aren't in the list projection so they fall
  // back to safe defaults.
  const buildPayload = (ev, draft, targetDate) => {
    const payload = {
      title: (draft.title || ev.title || "").trim() || "Untitled event",
      password: "admin",
      description: ev.description || draft.title || ev.title || "Event",
      date: longDate(targetDate),
      time: (draft.time || ev.time || "").trim() || "6:00 PM",
      length:
        ev.length != null && String(ev.length).trim() !== ""
          ? String(ev.length)
          : "2 hrs",
      location: ev.location || "join group for location",
      generalpublic: ev.generalpublic !== false,
      privateLocation:
        (draft.privateLocation || ev.privateLocation || ev.location || "").trim() ||
        "join group for location",
    };
    // NOTE: groupMeChatId / groupMeChatUrl are deliberately NOT
    // copied — they point at the ORIGINAL event's GroupMe subgroup,
    // and any GroupMe artifact on a row makes the event count as
    // "posted to GroupMe" (visible to everyone). Copies must start
    // unposted.
    const optionalPassthrough = [
      "imgKey",
      "chat",
      "groupmeImg",
      "pollPrivateLocation",
      "pollText",
    ];
    for (const key of optionalPassthrough) {
      const v = ev[key];
      if (typeof v === "string" && v.trim() !== "") {
        payload[key] = v;
      }
    }
    return payload;
  };

  const handleCopy = async () => {
    if (selectedIds.length === 0) return;
    if (
      !window.confirm(
        `Create ${selectedIds.length} event${
          selectedIds.length === 1 ? "" : "s"
        }, each shifted forward ${shiftDays} day${shiftDays === 1 ? "" : "s"}${
          shiftDays % 7 === 0 ? " (same weekdays)" : " (weekdays WILL shift)"
        }?`
      )
    ) {
      return;
    }
    setCopying(true);
    const headers = { "Content-Type": "application/json" };
    const token =
      typeof window !== "undefined" &&
      window.sessionStorage &&
      window.sessionStorage.getItem("cognitoIdToken");
    if (token) headers.Authorization = `Bearer ${token}`;

    for (const id of selectedIds) {
      const ev = events.find((e) => e.socialEventId === id);
      const draft = drafts[id];
      if (!ev || !draft) continue;
      const sourceDate = dateFromId(id);
      const targetDate = addDays(sourceDate, shiftDays);
      setDraft(id, { status: "copying", message: "" });
      try {
        const resp = await fetch(SOCIALEVENTS_POST_URL, {
          method: "POST",
          headers,
          body: JSON.stringify(buildPayload(ev, draft, targetDate)),
        });
        if (!resp.ok) {
          let msg = `HTTP ${resp.status}`;
          try {
            const out = await resp.json();
            // Joi failures arrive as structured objects — stringify
            // anything non-string so the row shows the actual reason
            // instead of "[object Object]".
            const raw = out?.error || out?.message || msg;
            msg =
              typeof raw === "string"
                ? raw
                : JSON.stringify(raw).slice(0, 300);
          } catch (e) {
            /* keep default */
          }
          throw new Error(msg);
        }
        setDraft(id, {
          status: "done",
          message: `Created for ${shortLabel(targetDate)}`,
          selected: false,
        });
      } catch (err) {
        setDraft(id, {
          status: "error",
          message: err.message || "Copy failed",
        });
      }
    }
    setCopying(false);
  };

  return (
    <div className="copyweek-tab">
      <div className="copyweek-tab__header">
        <h2 className="copyweek-tab__title">Copy Week Forward</h2>
        <button
          type="button"
          className="copyweek-tab__btn"
          onClick={load}
          disabled={loading || copying}
        >
          {loading ? "Loading…" : "Refresh"}
        </button>
      </div>
      <p className="copyweek-tab__hint">
        Pick a source date range (defaults to the last 7 days — any window
        works, up to 90 days back), tick the events to repeat, adjust
        title / time / location where needed, then copy them forward. The
        shift auto-picks the nearest future <strong>same-weekday</strong>{" "}
        week; override it for odd cases. Copies don't carry the source
        week's GroupMe poll or hosts — attach those via Edit Events
        afterwards if needed.
      </p>

      <div className="copyweek-tab__range">
        <label>
          From
          <input
            type="date"
            value={fromDate}
            min={toISO(addDays(todayMidnight(), -90))}
            onChange={(e) => setFromDate(e.target.value)}
            disabled={copying}
          />
        </label>
        <label>
          To
          <input
            type="date"
            value={toDate}
            min={toISO(addDays(todayMidnight(), -90))}
            onChange={(e) => setToDate(e.target.value)}
            disabled={copying}
          />
        </label>
        <label>
          Shift forward (days)
          <input
            type="number"
            min="1"
            step="1"
            value={shiftDays}
            onChange={(e) => {
              setShiftDirty(true);
              const n = parseInt(e.target.value, 10);
              setShiftDays(Number.isFinite(n) && n > 0 ? n : 1);
            }}
            disabled={copying}
          />
        </label>
        <span
          className={
            shiftDays % 7 === 0
              ? "copyweek-tab__weekday-ok"
              : "copyweek-tab__weekday-warn"
          }
        >
          {shiftDays % 7 === 0
            ? "✓ same weekdays"
            : "⚠ weekdays will shift"}
        </span>
        {shiftDirty && (
          <button
            type="button"
            className="copyweek-tab__btn"
            onClick={() => {
              setShiftDirty(false);
              setShiftDays(autoShiftFor(toDate));
            }}
            disabled={copying}
          >
            Auto
          </button>
        )}
      </div>

      {error && <div className="copyweek-tab__error">Error: {error}</div>}

      {!loading && !error && events.length === 0 && (
        <div className="copyweek-tab__empty">
          No events found in the last 7 days.
        </div>
      )}

      {events.length > 0 && (
        <>
          <div className="copyweek-tab__toolbar">
            <button
              type="button"
              className="copyweek-tab__btn"
              onClick={() => setAll(true)}
              disabled={copying}
            >
              Select all
            </button>
            <button
              type="button"
              className="copyweek-tab__btn"
              onClick={() => setAll(false)}
              disabled={copying}
            >
              Clear
            </button>
            <span className="copyweek-tab__count">
              {selectedIds.length} selected
            </span>
            <button
              type="button"
              className="copyweek-tab__btn copyweek-tab__btn--primary"
              onClick={handleCopy}
              disabled={copying || selectedIds.length === 0}
            >
              {copying
                ? "Copying…"
                : `Copy ${selectedIds.length || ""} to next week`}
            </button>
          </div>

          <div className="copyweek-tab__list">
            {events.map((ev) => {
              const id = ev.socialEventId;
              const draft = drafts[id] || {};
              const sourceDate = dateFromId(id);
              const targetDate = sourceDate
                ? addDays(sourceDate, shiftDays)
                : null;
              return (
                <div
                  key={id}
                  className={
                    "copyweek-tab__row" +
                    (draft.selected ? " copyweek-tab__row--selected" : "") +
                    (draft.status === "done" ? " copyweek-tab__row--done" : "")
                  }
                >
                  <label className="copyweek-tab__row-check">
                    <input
                      type="checkbox"
                      checked={!!draft.selected}
                      disabled={copying || draft.status === "copying"}
                      onChange={(e) =>
                        setDraft(id, { selected: e.target.checked })
                      }
                    />
                    <span className="copyweek-tab__dates">
                      {sourceDate ? shortLabel(sourceDate) : "?"}
                      <span className="copyweek-tab__arrow">→</span>
                      {targetDate ? shortLabel(targetDate) : "?"}
                    </span>
                  </label>

                  <div className="copyweek-tab__row-fields">
                    <div className="copyweek-tab__source-title">
                      {ev.title || id}
                    </div>
                    {draft.selected && (
                      <div className="copyweek-tab__edits">
                        <label>
                          Title
                          <input
                            type="text"
                            value={draft.title}
                            onChange={(e) =>
                              setDraft(id, { title: e.target.value })
                            }
                            disabled={copying}
                          />
                        </label>
                        <label>
                          Time
                          <input
                            type="text"
                            value={draft.time}
                            placeholder="6:00 PM"
                            onChange={(e) =>
                              setDraft(id, { time: e.target.value })
                            }
                            disabled={copying}
                          />
                        </label>
                        <label>
                          Private location
                          <input
                            type="text"
                            value={draft.privateLocation}
                            placeholder="Venue, address"
                            onChange={(e) =>
                              setDraft(id, { privateLocation: e.target.value })
                            }
                            disabled={copying}
                          />
                        </label>
                      </div>
                    )}
                    {draft.status === "done" && (
                      <div className="copyweek-tab__status copyweek-tab__status--done">
                        ✓ {draft.message}
                      </div>
                    )}
                    {draft.status === "error" && (
                      <div className="copyweek-tab__status copyweek-tab__status--error">
                        {draft.message}
                        <button
                          type="button"
                          className="copyweek-tab__btn"
                          style={{ marginLeft: 8 }}
                          onClick={() => setDraft(id, { status: "idle" })}
                        >
                          Dismiss
                        </button>
                      </div>
                    )}
                    {draft.status === "copying" && (
                      <div className="copyweek-tab__status">Creating…</div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
};

export default CopyWeekTab;
